import { Styles } from "@/shared/coralreef";
import { important, percent, px, rem, viewHeight } from "csx";
import { Content, Horizontal, Vertical } from "gls/lib";
import moment from "moment";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { SitesContext } from "@/shared/context/Sites";
import { classes, style } from "typestyle";
import { useContextSelector } from "use-context-selector";
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
} from "@/shared/components/accordion/Accordion";
import { MappedSite, MappedToolbarSite } from "../toolbar";
import { SitesListItem } from "./SitesListItem";

interface SitesListProps {
  search: string | undefined;
  open: boolean;
  isLoading: boolean;
  sites: MappedToolbarSite;
}

export const SitesList: FC<SitesListProps> = React.memo(
  ({ search, open, sites, isLoading }) => {
    const {
      selectSite,
      isSiteSelected,
      toggleCages,
      clearCages,
      selectedCages,
    } = useContextSelector(SitesContext, (sites) => ({
      selectedSites: sites.selectedSites,
      selectedCages: sites.selectedCages,
      selectSite: sites.selectSite,
      toggleCages: sites.toggleCages,
      isSiteSelected: sites.isSiteSelected,
      clearCages: sites.clearCages,
    }));

    const { t } = useTranslation();

    const fallowedSitesGroupedByYear = sites.fallowedSites.reduce(
      (acc, site) => {
        const year = moment(site.createdAt).format("YYYY").toString();

        if (acc[year]) {
          acc[year].push(site);
        } else {
          acc[year] = [site];
        }

        return acc;
      },
      {} as { [key: string]: MappedSite[] },
    );
    return (
      <Content>
        {open && <Caret />}
        <Vertical
          spacing={0}
          sizing="stretch"
          horizontalAlign="stretch"
          padding={[16, rem(1)]}
          verticalAlign="top"
          className={classes(
            Styles.scrollClass,
            style({
              position: "absolute",
              top: rem(3.5),
              right: 0,
              background: Styles.Colours.darkBlue,
              boxShadow: Styles.boxShadow,
              zIndex: 1000,
              display: open ? "flex" : important("none"),
              transition: "height 1s ease",
            }),
          )}
          width={px(230)}
          maxHeight={viewHeight(80)}
        >
          {sites.activeSites.length > 0 && (
            <Accordion allowsMultiple>
              {sites.activeSites.map((site) => (
                <Vertical width={percent(100)} spacing={0} key={`${site.name}`}>
                  <AccordionHeader key={`header-${site.id}`} id={site.id}>
                    <SitesListItem
                      text={site.name}
                      isHighlighted={isSiteSelected(site.id)}
                      onClick={() => selectSite(site.id)}
                    />
                  </AccordionHeader>
                  <AccordionContent key={`content-${site.id}`} id={site.id}>
                    <Content padding={[0, 0, 0, rem(0.75)]}>
                      <SitesListItem
                        disabled={!selectedCages?.[site.id]?.length}
                        text={t("Select All")}
                        onClick={() => clearCages(site.id)}
                      />
                      {site.cages.map((c, i) => (
                        <SitesListItem
                          key={`${site.id}-${c.cageId}-${i}`}
                          text={`Pen ${c.cageId}`}
                          isHighlighted={selectedCages?.[site.id]?.includes(
                            c.cageId,
                          )}
                          isDeactivated={c?.fallowedSite}
                          onClick={() => toggleCages(site.id, [c.cageId])}
                        />
                      ))}
                    </Content>
                  </AccordionContent>
                </Vertical>
              ))}
            </Accordion>
          )}

          {Object.keys(sites.fallowedSites).length > 0 && (
            <>
              {Object.entries(fallowedSitesGroupedByYear)
                .sort((siteA, siteB) => sortByYear(siteA[0], siteB[0]))
                .map(([year, sites]) => (
                  <Accordion key={year}>
                    <AccordionHeader id={year} forceOpen={!!search}>
                      <div
                        style={{
                          display: "block",
                          marginBlockStart: "1.33em",
                          marginBlockEnd: "1.33em",
                          marginInlineStart: "0px",
                          marginInlineEnd: "0px",
                          fontWeight: "bold",
                          unicodeBidi: "isolate",
                        }}
                      >
                        {year}
                      </div>
                    </AccordionHeader>
                    <AccordionContent id={year} forceOpen={!!search}>
                      <Accordion>
                        {sites.map((site) => (
                          <Vertical
                            width={percent(100)}
                            spacing={0}
                            key={`${site.name}`}
                          >
                            <AccordionHeader
                              key={`header-${site.id}`}
                              id={site.id}
                            >
                              <SitesListItem
                                text={site.name}
                                isHighlighted={isSiteSelected(site.id)}
                                onClick={() => selectSite(site.id)}
                              />
                            </AccordionHeader>
                            <AccordionContent
                              key={`content-${site.id}`}
                              id={site.id}
                            >
                              <Content padding={[0, 0, 0, rem(0.75)]}>
                                <SitesListItem
                                  disabled={!selectedCages?.[site.id]?.length}
                                  text={t("Select All")}
                                  onClick={() => clearCages(site.id)}
                                />
                                {site.cages.map((c, i) => (
                                  <SitesListItem
                                    key={`${site.id}-${c.cageId}-${i}`}
                                    text={`Pen ${c.cageId}`}
                                    isHighlighted={selectedCages?.[
                                      site.id
                                    ]?.includes(c.cageId)}
                                    isDeactivated={c?.fallowedSite}
                                    onClick={() =>
                                      toggleCages(site.id, [c.cageId])
                                    }
                                  />
                                ))}
                              </Content>
                            </AccordionContent>
                          </Vertical>
                        ))}
                      </Accordion>
                    </AccordionContent>
                  </Accordion>
                ))}
            </>
          )}

          {sites.activeSites.length === 0 &&
            Object.keys(sites.fallowedSites).length === 0 && (
              <>
                <Horizontal horizontalAlign="center" verticalAlign="center">
                  {isLoading
                    ? t("Loading sites")
                    : search
                      ? t("No results")
                      : t("No sites available")}
                </Horizontal>
              </>
            )}
        </Vertical>
      </Content>
    );
  },
);

const Caret = () => (
  <svg
    role="presentation"
    focusable="false"
    className={style({
      position: "absolute",
      height: px(10),
      width: px(20),
      top: px(46),
      right: px(50),
    })}
  >
    <path
      className={style({ fill: Styles.Colours.blue })}
      d="M0,10 20,10 10,0z"
    ></path>
    <path
      className={style({ fill: Styles.Colours.blue })}
      d="M0,10 10,0 20,10"
    ></path>
  </svg>
);

const sortByYear = (yearA: string, yearB: string) => {
  return moment(yearB).diff(moment(yearA));
};
